import { setSelectedQueryModal } from "redux/SingleDataset/actions";
import { useEffect, useMemo, useRef, useState } from "react";
import { QueryOperator, ExactMatchQueryOperators, VqlQuery } from "types";
import { useDispatch } from "react-redux";

import QueryModal from "views/components/QueryModal";
import QueryModalInput from "views/components/QueryModal/QueryModalInput";
import QueryModalTitle from "views/components/QueryModal/QueryModalTitle";
import SubmitQueryButton from "views/components/QueryModal/SubmitQueryButton";
import QueryModalOperators from "views/components/QueryModal/QueryModalOperators";
import useVqlParams from "hooks/useVqlParams";
import {
  createAndUpdateUserTagsFilter,
  isVqlQuery,
  getQueryValuesForFilter,
} from "helpers/utility/vqlHelpers";

import UserTagIcon from "assets/icons/UserTagIcon";
import styles from "./style.module.scss";
import {
  convertAPIOperator,
  handleQuerySelection,
} from "helpers/utility/queryFilterHelpers";
import { useMultiSelectQueryModal } from "hooks/UseQueryModal/useMultiSelectQueryModal";
import { UserTagItem, useUserTagsData } from "./useUserTagsData";

const USERTAGS_OPERATORS: QueryOperator[] = [
  QueryOperator.IS,
  QueryOperator.IS_NOT,
  QueryOperator.IS_ONE_OF,
];

interface UserTagsQueryModalProps {
  editQueryId?: string;
}

export default function UserTagsQueryModal({
  editQueryId,
}: UserTagsQueryModalProps) {
  const { userTags: userTagsData, isLoading } = useUserTagsData();
  const {
    addVqlQuery,
    findVqlQuery,
    updateVqlQuery,
    removeVqlQuery,
    vqlQueries,
  } = useVqlParams();

  const oldQuery = editQueryId ? findVqlQuery(editQueryId, "tags") : null;
  const queryOperator = oldQuery
    ? convertAPIOperator(oldQuery.tags.op)
    : QueryOperator.IS_ONE_OF;

  const [selectedOperator, setSelectedOperator] = useState(queryOperator);
  const existingQuery = useMemo(() => {
    return (
      vqlQueries.find(
        (query: VqlQuery) =>
          isVqlQuery(query, "tags") &&
          convertAPIOperator(query.tags?.op) === selectedOperator,
      ) || null
    );
  }, [vqlQueries, selectedOperator]);

  const queryValues = useMemo(() => {
    return getQueryValuesForFilter(
      editQueryId,
      oldQuery,
      selectedOperator,
      queryOperator,
      existingQuery,
      "tags",
    );
  }, [editQueryId, oldQuery, selectedOperator, queryOperator, existingQuery]);

  const userTags = userTagsData.map((tags: UserTagItem) => ({
    ...tags,
    selected: queryValues.includes(tags.id),
  }));

  const selectedUserTagIds = userTags
    .filter((tag: UserTagItem) => tag.selected)
    .map((tag: UserTagItem) => tag.id);

  const [tags, setTags] = useState(userTags);
  const [searchText, setSearchText] = useState("");

  const modalRef = useRef<HTMLDivElement>(null);
  const dispatch = useDispatch();

  useEffect(() => {
    setTags(userTags);
    // eslint-disable-next-line
  }, [selectedOperator, isLoading]);

  const selectedTagIds = tags
    .filter((tag: UserTagItem) => tag.selected)
    .map((tag: UserTagItem) => tag.id);

  const { isSubmitDisabled } = useMultiSelectQueryModal({
    selectedIds: selectedTagIds,
    previousIds: selectedUserTagIds,
    selectedOperator,
    initialOperator: queryOperator,
    editQueryId,
    existingQueryId: existingQuery?.id,
  });

  const handleRemoveTag = (id: number | string) => {
    const newTags = tags.map((tag) => {
      if (tag.id === id) {
        return { ...tag, selected: false };
      }

      return tag;
    });
    setTags(newTags);
  };

  const handleSelectTag = (id: number | string) => {
    const newTags = tags.map((tag) => {
      if (tag.id === id) {
        return { ...tag, selected: !tag.selected };
      }
      return tag;
    });

    setTags(newTags);
  };

  const handleOptionSelection = (operator: QueryOperator) => {
    setSelectedOperator(operator);
  };

  const handleSubmission = () => {
    if (isSubmitDisabled) {
      return;
    }

    // If no selections, remove the query
    if (selectedTagIds.length === 0) {
      handleQuerySelection({
        editQueryId,
        existingQueryId: existingQuery?.id,
        removeVqlQuery,
        dispatch,
      });
      return;
    }

    const vqlQuery = createAndUpdateUserTagsFilter(
      tags
        .filter((tag) => selectedTagIds.includes(tag.id))
        .map((tag) => tag.id + ""),
      selectedOperator as ExactMatchQueryOperators,
      editQueryId || existingQuery?.id,
    );

    editQueryId || existingQuery
      ? updateVqlQuery(vqlQuery)
      : addVqlQuery(vqlQuery);
    dispatch(setSelectedQueryModal(null));
  };

  const handleInputChange = (value: string) => {
    setSearchText(value);
  };

  return (
    <QueryModal modalRef={modalRef}>
      <QueryModalTitle icon={<UserTagIcon />}>User Tags</QueryModalTitle>
      <div className={styles.filterContent}>
        <QueryModalOperators
          operators={[...USERTAGS_OPERATORS]}
          selectedOperator={selectedOperator}
          onSelect={handleOptionSelection}
        />
        <QueryModalInput
          mode="multiple"
          options={tags}
          selected={selectedTagIds}
          searchText={searchText}
          onRemove={handleRemoveTag}
          onSelect={handleSelectTag}
          onInputChange={handleInputChange}
          isLoading={isLoading}
        />

        <SubmitQueryButton
          isDisabled={isSubmitDisabled}
          onSubmit={handleSubmission}
        />
      </div>
    </QueryModal>
  );
}
